import React, { useState } from 'react';
import { useTranslations } from '../../locales/useTranslations';
import { useRouter } from 'next/router';
import KarriereJobDetailModal from './KarriereJobDetailModal';
import KarriereNoJobsAvailable from './KarriereNoJobsAvailable';
import KarriereJobCard from './KarriereJobCard';

// Mapping von Tab-IDs zu Typen
const TAB_TYPE_MAP = {
    '1': 'praktika',
    '2': 'ausbildung',
    '3': 'stellen',
    '4': 'thesis'
};

const KarriereJobsTabs = ({ jobs = [], isLoading = false }) => {
    const { t } = useTranslations();
    const router = useRouter();
    const locale = router.locale || 'de';
    const [activeTab, setActiveTab] = useState('1');
    const [selectedJob, setSelectedJob] = useState(null);
    const [selectedTranslation, setSelectedTranslation] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const tabs = [
        { id: '1', label: t('career_tabs_internships') },
        { id: '2', label: t('career_tabs_apprenticeship') },
        { id: '3', label: t('career_tabs_positions') },
        { id: '4', label: t('career_tabs_thesis') }
    ];

    // Hilfsfunktion: Übersetzung für aktuelle Sprache finden
    const getTranslation = (job) => {
        if (!job.translations || job.translations.length === 0) return null;

        const langCode = locale === 'de' ? 'de-DE' : 'en-US';
        const translation = job.translations.find(t => t.languages_code === langCode);

        // Fallback auf erste Übersetzung
        return translation || job.translations[0];
    };

    // Jobs nach Typ filtern (nur enabled und nicht abgelaufen)
    const getJobsByType = (type) => {
        const now = new Date();
        return jobs.filter(job => {
            if (job.type !== type) return false;
            if (!job.enabled) return false;
            if (job.validTill && new Date(job.validTill) < now) return false;
            return true;
        });
    };

    // Kategorie-spezifische Übersetzungskeys
    const getCategoryContent = (tabId) => {
        const categoryMap = {
            '1': {
                title: t('career_internships_title'),
                desc1: t('career_internships_description1'),
                desc2: t('career_internships_description2')
            },
            '2': {
                title: t('career_apprenticeship_title'),
                desc1: t('career_apprenticeship_description1'),
                desc2: t('career_apprenticeship_description2')
            },
            '3': {
                title: t('career_positions_title'),
                desc1: t('career_positions_description1'),
                desc2: t('career_positions_description2')
            },
            '4': {
                title: t('career_thesis_title'),
                desc1: t('career_thesis_description1'),
                desc2: t('career_thesis_description2')
            }
        };
        return categoryMap[tabId] || categoryMap['1'];
    };

    // Modal öffnen
    const handleShowDetails = (job) => {
        const translation = getTranslation(job);
        setSelectedJob(job);
        setSelectedTranslation(translation);
        setIsModalOpen(true);
    };

    // Modal schließen
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedJob(null);
        setSelectedTranslation(null);
    };

    // Ladeanimation rendern
    const renderLoadingState = () => {
        return (
            <div className="col-12">
                <div className="jobs-loading">
                    <div className="jobs-loading__spinner">
                        <div className="jobs-loading__dot"></div>
                        <div className="jobs-loading__dot"></div>
                        <div className="jobs-loading__dot"></div>
                    </div>
                    <p className="jobs-loading__text">{t('career_jobs_loading')}</p>
                </div>
            </div>
        );
    };

    // Render Jobs für einen Typ
    const renderJobCards = (type) => {
        if (isLoading) {
            return renderLoadingState();
        }

        const typeJobs = getJobsByType(type);

        if (typeJobs.length === 0) {
            return (
                <div className="col-12">
                    <KarriereNoJobsAvailable categoryTitle={getCategoryContent(activeTab).title} />
                </div>
            );
        }

        return typeJobs.map((job) => {
            const translation = getTranslation(job);

            return (
                <KarriereJobCard
                    key={job.id}
                    job={job}
                    translation={translation}
                    onShowDetails={() => handleShowDetails(job)}
                />
            );
        });
    };

    return (
        <>
            <div id="jobs" className="jobs-tabs-area pt-100 pb-70">
                <div className="jobs-decoration-circle jobs-decoration-circle--top-right"></div>
                <div className="jobs-decoration-circle jobs-decoration-circle--bottom-left"></div>

                <div className="container" style={{ position: 'relative', zIndex: '2' }}>
                    <div className="section-title text-center mb-5">
                        <span className="jobs-section-badge">
                            {t('career_jobs_title')}
                        </span>
                        <h2 className="jobs-section-title">
                            {t('career_jobs_subtitle')}
                        </h2>
                    </div>

                    <div className="update-screen-tab">
                        <div className="custom-tabs">
                            <div className="jobs-tabs-header">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        className={`jobs-tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className="tab-content">
                                {tabs.map((tab) => (
                                    activeTab === tab.id && (
                                        <div key={tab.id} className="contact-inner">
                                            <div className="jobs-overview-item">
                                                <div className="row">
                                                    <h4>{getCategoryContent(tab.id).title}</h4>
                                                    <p>{getCategoryContent(tab.id).desc1}</p>
                                                    <p>{getCategoryContent(tab.id).desc2}</p>
                                                </div>
                                            </div>
                                            <div className="row">
                                                {renderJobCards(TAB_TYPE_MAP[tab.id])}
                                            </div>
                                        </div>
                                    )
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Job Detail Modal */}
            <KarriereJobDetailModal
                job={selectedJob}
                translation={selectedTranslation}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
        </>
    );
}

export default KarriereJobsTabs;
